import React, { useState, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuLoaderCircle, LuPencil } from 'react-icons/lu';
import { doc, updateDoc } from 'firebase/firestore'; // Import updateDoc
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import { Field, SelectInput, TextInput } from '../../components/form/FormControls';
import useBudgetText from './budgetText';

// Initial empty state matching the AddExpenseModal structure
const initialFormData = {
    BütçeAdı: '',
    BütçeKaynak: '',
    BütçeSahibi: '',
    BütçeTipi: 'Gider', // Default type to Gider for expense
    BütçeKategorisi: '',
    GiderTipi: '', // This will be selected by the user
    ParaBirimi: 'TRY',
    KDV: '', // Keep KDV if needed for expenses
    BütçeMiktarı: '',
    BütçeTarihi: '',
    BeklenenÖdemeTarihi: '', // Keep if needed
    ÖdemeDurumu: 'Beklemede' // Default status
};

/**
 * "Gider Düzenle" (Tailwind): the summary fields of a record (Bütçe*). The
 * update logic is the old one; values outside the option lists stay selected.
 */
const EditExpenseModal = ({ isOpen, onClose, onSubmit, budgetData }) => {
  const { t, language, label, statusLabel } = useBudgetText();
  const uid = useId();
  // Toasts and messages read the current language through a ref
  const labelRef = useRef(label);
  labelRef.current = label;
  const msg = (key, vars) => labelRef.current(`toast.${key}`, vars);
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when budgetData is available or changes
  useEffect(() => {
    if (budgetData) {
        const populatedData = { ...initialFormData };
        for (const key in initialFormData) {
            if (budgetData.hasOwnProperty(key)) {
               populatedData[key] = budgetData[key];
            }
        }
        // Ensure type is set correctly if somehow missing in old data
        populatedData.BütçeTipi = 'Gider'; 
        setFormData(populatedData);
    } else {
      setFormData(initialFormData);
    }
  }, [budgetData, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!budgetData || !budgetData.id) {
        toast.error(msg('expenseNotFound'));
        return;
    }
    setIsSubmitting(true);

    // Prepare data for update (exclude id, ensure types)
    const { id, createdAt, ...dataToUpdate } = formData;
    dataToUpdate.BütçeTipi = 'Gider'; // Ensure type is Gider
    // GiderTipi comes from the form selection

    const budgetRef = doc(db, 'Budgets', budgetData.id);

    try {
      await updateDoc(budgetRef, dataToUpdate);

      toast.success(msg('expenseUpdated'));
      onSubmit(budgetData.id, dataToUpdate); // Pass ID and updated data back
      onClose();

    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error(msg('expenseUpdateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!budgetData) return null;

  const formId = `${uid}-form`;
  const choose = label('form.choose');
  const options = (values) => values.map((value) => ({ value, label: value }));
  // A stored value outside these lists (e.g. an e-invoice title) stays selected instead of blocking the form
  const select = (name, values, extra = {}) => (
    <SelectInput
      id={`${uid}-${name}`}
      name={name}
      value={formData[name] || ''}
      onChange={handleChange}
      required
      current={formData[name] || ''}
      options={options(values)}
      {...extra}
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={label('form.titleEditExpense')}
      subtitle={budgetData['BütçeAdı'] || undefined}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuPencil aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{label('common.cancel')}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={isSubmitting} className="max-sm:tw-flex-1">
            {isSubmitting
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" strokeWidth={3} />}
            {isSubmitting ? label('form.updating') : label('form.updateExpense')}
          </Button>
        </>
      )}
    >
      <form id={formId} onSubmit={handleSubmit} className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={label('form.expenseName')} htmlFor={`${uid}-BütçeAdı`} required className="sm:tw-col-span-2">
          <TextInput id={`${uid}-BütçeAdı`} name="BütçeAdı" value={formData.BütçeAdı || ''} onChange={handleChange} required placeholder={label('form.expenseNamePlaceholder')} />
        </Field>
        <Field label={label('form.source')} htmlFor={`${uid}-BütçeKaynak`} required>
          {select('BütçeKaynak', ['Şirket Kasası', 'Kredi Kartı', 'Banka Hesabı'], { placeholder: choose })}
        </Field>
        <Field label={label('form.spender')} htmlFor={`${uid}-BütçeSahibi`} required>
          {select('BütçeSahibi', ['Personel A', 'Personel B', 'Yönetici'], { placeholder: choose })}
        </Field>
        <Field label={label('form.expenseType')} htmlFor={`${uid}-GiderTipi`} required>
          {select('GiderTipi', ['Ofis Malzemesi', 'Seyahat', 'Yemek', 'Fatura', 'Diğer'], { placeholder: choose })}
        </Field>
        <Field label={label('form.category')} htmlFor={`${uid}-BütçeKategorisi`} required>
          {select('BütçeKategorisi', ['Operasyonel', 'Pazarlama', 'İnsan Kaynakları'], { placeholder: choose })}
        </Field>
        <Field label={label('columns.amount')} htmlFor={`${uid}-BütçeMiktarı`} required>
          <TextInput id={`${uid}-BütçeMiktarı`} type="number" name="BütçeMiktarı" value={formData.BütçeMiktarı || ''} onChange={handleChange} required placeholder={label('form.amountPlaceholder')} step="0.01" className="tw-tabular-nums" />
        </Field>
        <Field label={label('columns.currency')} htmlFor={`${uid}-ParaBirimi`} required>
          {select('ParaBirimi', ['TRY', 'USD', 'EUR'])}
        </Field>
        <Field label={label('form.expenseDate')} htmlFor={`${uid}-BütçeTarihi`} required>
          <TextInput id={`${uid}-BütçeTarihi`} type="date" name="BütçeTarihi" value={formData.BütçeTarihi || ''} onChange={handleChange} required />
        </Field>
        <Field label={label('columns.paymentStatus')} htmlFor={`${uid}-ÖdemeDurumu`} required>
          {select('ÖdemeDurumu', ['Ödendi', 'Beklemede', 'İptal'], { options: ['Ödendi', 'Beklemede', 'İptal'].map((value) => ({ value, label: statusLabel(value) })) })}
        </Field>
      </form>
    </Modal>
  );
};

EditExpenseModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  budgetData: PropTypes.object,
};

export default EditExpenseModal;
